import { useEffect, useRef } from "react";
import { Controller, useController, useFormContext } from "react-hook-form";
import {
  baremo,
  MAX_MIN,
  MIN_S,
  toYMD,
  todayYMD,
} from "./rhfParameterInputsShared.js";

export { toYMD, todayYMD };

function preventNumberInputSpinKeyDown(e) {
  if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
}

/**
 * Número con etiqueta, sufijo y error (Yup vía yupResolver en el form).
 * `description`: texto opcional debajo del input (p. ej. ayuda contextual).
 * Requiere `FormProvider` alrededor del formulario.
 */
export const RhfNumberFieldWithSuffix = ({
  name,
  label,
  suffix,
  description,
  step = "0.01",
  min = 0,
  max,
  inputMode = "decimal",
  idPrefix = "rhf",
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  const fieldId = `${idPrefix}-${name}`;
  const errId = `${idPrefix}-err-${name}`;
  const descId = `${idPrefix}-desc-${name}`;
  const hasDescription = description != null && description !== "";
  const describedByIds = [
    hasDescription ? descId : null,
    fieldState.error ? errId : null,
  ]
    .filter(Boolean)
    .join(" ");
  const inputElRef = useRef(null);
  const setInputRef = (el) => {
    inputElRef.current = el;
    field.ref(el);
  };
  useEffect(() => {
    const el = inputElRef.current;
    if (!el) return;
    const onWheel = (e) => e.preventDefault();
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className={baremo.field}>
      <label className={baremo.label} htmlFor={fieldId}>
        {label}
      </label>
      <div className={baremo.inputWrap}>
        <input
          id={fieldId}
          type="number"
          inputMode={inputMode}
          step={step}
          min={min}
          max={max}
          className={baremo.input}
          name={field.name}
          onBlur={field.onBlur}
          ref={setInputRef}
          onKeyDown={preventNumberInputSpinKeyDown}
          value={
            field.value === "" || field.value == null
              ? ""
              : String(field.value)
          }
          onChange={(e) => {
            const v = e.target.value;
            field.onChange(
              v === "" ? 0 : Number.isFinite(parseFloat(v)) ? parseFloat(v) : 0
            );
          }}
          aria-invalid={fieldState.invalid}
          aria-describedby={describedByIds || undefined}
        />
        <span className={baremo.suffix} aria-hidden>
          {suffix}
        </span>
      </div>
      {hasDescription ? (
        <p id={descId} className={baremo.fieldDescription}>
          {description}
        </p>
      ) : null}
      {fieldState.error?.message ? (
        <span
          id={errId}
          className={baremo.error}
          role="alert"
        >
          {fieldState.error.message}
        </span>
      ) : null}
    </div>
  );
};

export const RhfPercentField = ({
  name,
  label,
  idPrefix = "rhf",
  description,
}) => (
  <RhfNumberFieldWithSuffix
    name={name}
    label={label}
    suffix="%"
    step="1"
    min={0}
    max={100}
    inputMode="numeric"
    idPrefix={idPrefix}
    description={description}
  />
);

export const RhfMoneyField = ({
  name,
  label,
  suffix,
  idPrefix = "rhf",
  description,
}) => (
  <RhfNumberFieldWithSuffix
    name={name}
    label={label}
    suffix={suffix}
    step="0.01"
    min={0}
    idPrefix={idPrefix}
    description={description}
  />
);

/**
 * Fecha AAAA-MM-DD (valor interno y visual para `type="date"`).
 * `formatValue` convierte el valor almacenado (p. ej. ISO) al string del input.
 * Requiere `FormProvider`.
 */
export const RhfDateYmdField = ({
  name,
  label,
  formatValue = toYMD,
  id = "rhf-date",
  idPrefix = "rhf",
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ name, control });
  const ymd = formatValue(field.value);
  const errId = `${idPrefix}-err-${name}`;

  return (
    <div className={baremo.fieldDate}>
      <label className={baremo.label} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="date"
        className={baremo.inputDate}
        name={field.name}
        onBlur={field.onBlur}
        ref={field.ref}
        value={ymd}
        onChange={(e) => field.onChange(e.target.value)}
        autoComplete="off"
        aria-invalid={fieldState.invalid}
        aria-describedby={fieldState.error ? errId : undefined}
      />
      {fieldState.error?.message ? (
        <span id={errId} className={baremo.error} role="alert">
          {fieldState.error.message}
        </span>
      ) : null}
    </div>
  );
};

/**
 * Minutos 0–999 (texto + sufijo "min") con validación cruzada opcional.
 * Pasa `control` desde el `useForm` padre (no usa `FormProvider`).
 */
export const RhfAlarmMinutesField = ({
  control,
  name,
  ariaLabel,
  trigger: triggerForm,
  revalidateFieldNames = [],
  disabled,
  readOnlyContent,
}) => {
  if (disabled) {
    return readOnlyContent ?? null;
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div>
          <div className="alarm-times-section__min-input-wrap">
            <input
              type="text"
              inputMode="numeric"
              className="alarm-times-section__min-input"
              value={
                field.value != null && field.value !== ""
                  ? String(field.value)
                  : ""
              }
              onChange={async (e) => {
                const raw = e.target.value;
                if (raw === "") {
                  field.onChange("");
                } else {
                  const n = parseInt(String(raw).replace(/\D/g, ""), 10);
                  const next = Number.isFinite(n)
                    ? Math.min(Math.max(n, MIN_S), MAX_MIN)
                    : 0;
                  field.onChange(next);
                }
                if (revalidateFieldNames.length && triggerForm) {
                  await triggerForm(revalidateFieldNames);
                }
              }}
              onBlur={field.onBlur}
              name={field.name}
              ref={field.ref}
              aria-label={ariaLabel}
            />
            <span className="alarm-times-section__min-suffix">min</span>
          </div>
          {fieldState.error?.message ? (
            <span
              className="text-danger d-block small mt-1"
              role="alert"
            >
              {fieldState.error.message}
            </span>
          ) : null}
        </div>
      )}
    />
  );
};
